export const accountantsData = {
  industry: "Accountants",
  slug: "accountants",
  
  // SEO Meta Tags
  seo: {
    title: "AI Intake Forms for Accountants | HeyIntake",
    description: "AI-powered intake forms for accounting firms. Qualify prospects, collect financial details, and streamline tax season client onboarding.",
    keywords: [
      "accounting client intake form",
      "tax client onboarding",
      "CPA intake software",
      "accounting firm lead qualification",
      "bookkeeping client intake",
      "tax season intake management"
    ]
  },

  heroTitle: "Client Intake Forms for Accounting Professionals",
  heroSubtitle: "Qualify prospects, collect financial details, and onboard new clients efficiently.",
  heroDescription: "Whether you specialize in tax preparation, bookkeeping, audit, or advisory services, HeyIntake helps you gather the information you need to serve clients effectively.",
  
  // NEW: Why AI Scoring section
  whyAIScoring: {
    title: "Why Accountants Need Better Client Intake",
    intro: "Tax season is stressful enough without chasing clients for documents or discovering mid-engagement that their situation is far more complex than anticipated.",
    paragraphs: [
      "Traditional accounting intake is a bottleneck. Simple questionnaires don't capture the full picture. Staff spend hours on follow-up calls gathering missing information. Complex clients get mixed in with simple 1040s, and you don't realize the mismatch until you're deep into the work.",
      "HeyIntake's AI-powered intake forms solve these problems. Clients provide comprehensive financial details, document access, and complexity indicators upfront. Our adaptive scoring evaluates each inquiry to help you identify high-value business clients who need partner attention versus routine work suitable for staff.",
      "For CPAs, tax professionals, bookkeepers, and accounting firms of all sizes, this means smoother tax seasons, better client matching, and more profitable engagements with less administrative overhead."
    ]
  },
  
  painPoints: [
    {
      title: "Incomplete Financial Pictures",
      description: "Starting engagements without complete information about the client's financial situation."
    },
    {
      title: "Tax Season Chaos",
      description: "Scrambling to collect documents and details from clients during the busiest time of year."
    },
    {
      title: "Scope Mismatch",
      description: "Discovering the client's needs are much more complex than anticipated after agreeing to fees."
    },
    {
      title: "Chasing Documents",
      description: "Endless follow-ups to collect tax forms, bank statements, and financial records."
    },
    {
      title: "Can't Prioritize Inquiries",
      description: "Simple 1040 requests mixed with complex business clients requiring different attention."
    },
    {
      title: "No Conflict Screening",
      description: "Missing potential conflicts of interest until deep into the engagement process."
    }
  ],

  exampleFields: [
    {
      category: "Contact & Business Info",
      fields: [
        "Name and contact information",
        "Business name (if applicable)",
        "Entity type (individual, LLC, S-Corp, etc.)",
        "Industry or occupation",
        "How did you hear about us?"
      ]
    },
    {
      category: "Service Requirements",
      fields: [
        "Services needed (tax prep, bookkeeping, advisory, etc.)",
        "Current accounting software",
        "Fiscal year end date",
        "Do you have a current accountant?",
        "Why are you looking for a new accountant?"
      ]
    },
    {
      category: "Financial Overview",
      fields: [
        "Approximate annual revenue",
        "Number of employees",
        "Number of transactions per month",
        "Multi-state operations?",
        "International considerations?"
      ]
    },
    {
      category: "Documents & Access",
      fields: [
        "Prior year tax returns available?",
        "QuickBooks/accounting access",
        "Bank statement access",
        "Outstanding IRS notices?"
      ]
    }
  ],

  // Example intake form section
  exampleIntakeForm: {
    title: "Example Accounting Intake Form",
    description: "Here's what an effective accounting client intake looks like:",
    fields: [
      { label: "What service do you need?", type: "select", options: ["Tax preparation (personal)", "Tax preparation (business)", "Bookkeeping", "Payroll", "CFO/Advisory", "Audit/Review", "Multiple services"] },
      { label: "Business type", type: "select", options: ["Individual/Personal", "Sole proprietor", "LLC", "S-Corp", "C-Corp", "Partnership", "Non-profit"] },
      { label: "Annual revenue (if business)", type: "select", options: ["Under $100K", "$100K-$500K", "$500K-$1M", "$1M-$5M", "$5M+", "N/A - Personal"] },
      { label: "Current accounting software", type: "select", options: ["QuickBooks", "Xero", "FreshBooks", "Spreadsheets", "None", "Other"] },
      { label: "Do you have a current accountant?", type: "select", options: ["No", "Yes - switching", "Yes - need additional services"] },
      { label: "Timeline for service", type: "select", options: ["Tax deadline approaching", "Within 30 days", "Within 3 months", "Just exploring"] },
      { label: "Describe your situation", type: "textarea" }
    ],
    cta: "Use this exact form → Start for $1"
  },

  // NEW: How It Works section
  howItWorks: {
    title: "How HeyIntake Works for Accountants",
    subtitle: "From inquiry to prepared engagement in three simple steps",
    steps: [
      {
        title: "Clients Provide Details",
        description: "New clients share financial situation, service needs, and document access through your intake form."
      },
      {
        title: "AI Assesses Complexity",
        description: "Our AI evaluates engagement complexity, flags potential issues, and identifies service fit."
      },
      {
        title: "Start Engagements Prepared",
        description: "Begin every client relationship with complete information and clear expectations."
      }
    ]
  },

  aiScoringExplanation: {
    title: "AI-Powered Client Qualification for Accountants",
    description: "HeyIntake's AI evaluates each inquiry to help you prioritize high-value clients and identify complex situations that require senior attention.",
    factors: [
      "Revenue size and complexity indicators",
      "Service scope alignment with your expertise",
      "Urgency signals (tax deadlines, audits, etc.)",
      "Document and access readiness",
      "Potential conflict of interest flags"
    ],
    exampleLead: {
      name: "Greenfield Properties LLC",
      context: "Real Estate • $2M Revenue • Tax + Advisory",
      summary: "Multi-property real estate LLC seeking tax planning and quarterly advisory. Current accountant retiring. Clean books, responsive owner. Excellent fit for business services tier.",
      score: 86
    }
  },

  useCases: [
    {
      icon: "brain",
      title: "Score Client Complexity",
      description: "AI identifies complex engagements requiring partner attention vs. routine work for staff."
    },
    {
      icon: "file",
      title: "Collect Documents Upfront",
      description: "Request tax forms, financial statements, and access credentials before engagement begins."
    },
    {
      icon: "clock",
      title: "Streamline Tax Season",
      description: "Automated intake during busy season ensures you have what you need when you need it."
    },
    {
      icon: "users",
      title: "Screen for Fit",
      description: "Identify clients who match your firm's specialties and service capacity."
    }
  ],

  // Cross-linking to related industries
  relatedIndustries: [
    { name: "Intake Forms for Financial Advisors", path: "/intake-form-for-financial-advisors" },
    { name: "Intake Forms for Lawyers", path: "/intake-form-for-lawyers" },
    { name: "Intake Forms for Consultants", path: "/intake-form-for-consultants" },
    { name: "Intake Forms for Contractors", path: "/intake-form-for-contractors" }
  ],

  ctaText: "Ready to Onboard Clients More Efficiently?"
};
